import Swiper from 'swiper';
import { screens } from '../../../../../theme.config';
import {
    Navigation, Pagination, Scrollbar, Autoplay, Controller,
    Mousewheel, Keyboard, Zoom, Thumbs, FreeMode, Grid,
    HashNavigation, History, A11y,
    EffectFade, EffectCube, EffectFlip, EffectCoverflow, EffectCards, EffectCreative,
    Manipulation, Virtual, Parallax,
} from 'swiper/modules';
import './vendor.css';
import './styles.css';
import { loadSvg } from '../utils';

const SWIPER_SELECTOR = '.swiper';
const SWIPER_SYNC_SELECTOR = '.swiper-sync';
const BOUND_KEY = 'true';

let initialized = false;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

export function initSwiper(): void {
    if (initialized) return;
    initialized = true;
    document.addEventListener('ui:update', () => mount(document));
    document.addEventListener('ui:resize', handleWindowResize);
}

function handleWindowResize(): void {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
        document.querySelectorAll<HTMLElement>(SWIPER_SELECTOR).forEach(el => {
            const swiper = (el as any).swiper as InstanceType<typeof Swiper> | undefined;
            if (swiper && !swiper.destroyed) swiper.update();
        });
    }, 200);
}

function mount(scope: ParentNode): void {
    const swipers = Array.from(scope.querySelectorAll<HTMLElement>(SWIPER_SELECTOR));

    swipers.forEach((swiperEl) => {
        if (swiperEl.dataset.swiperBound === BOUND_KEY) return;
        swiperEl.dataset.swiperBound = BOUND_KEY;

        const options = createSwiperOptions(swiperEl);
        const hasShortAutoplay = options.autoplay?.delay < 500;

        if (hasShortAutoplay) {
            (options.autoplay as any).defaultDelay = options.autoplay.delay;
            (options.autoplay as any).delay = 500;
        }

        const swiper = new Swiper(swiperEl, options);

        if (swiper && hasShortAutoplay && typeof swiper.params.autoplay === 'object') {
            (swiper.params.autoplay as any).delay = (options.autoplay as any).defaultDelay;
        }
    });

    // Swiperを同期させる（二つまで）
    const swiperSyncs = Array.from(scope.querySelectorAll<HTMLElement>(SWIPER_SYNC_SELECTOR));
    if (swiperSyncs.length > 1) {
        const swipers = swiperSyncs
            .map(el => (el as any).swiper)
            .filter(Boolean)
            .slice(0, 2);
        if (swipers.length === 2) {
            swipers.forEach(swiper => {
                swiper.controller.control = swipers.filter(
                    otherSwiper => otherSwiper !== swiper
                );
            });
        }
    }
}

/**
 * Swiperのオプションオブジェクトを生成する
 * @param {HTMLElement} swiperEl - Swiperのコンテナ要素
 * @returns {object} - マージされたSwiperオプション
 */
function createSwiperOptions(swiperEl: HTMLElement) {
    // 共通オプション
    const commonOptions = {
        modules: [
            Navigation, Pagination, Scrollbar, Autoplay, Controller,
            Mousewheel, Keyboard, Zoom, Thumbs, FreeMode, Grid,
            HashNavigation, History, A11y,
            EffectFade, EffectCube, EffectFlip, EffectCoverflow, EffectCards, EffectCreative,
            Manipulation, Virtual, Parallax,
        ],
        watchOverflow: true,
    };

    // data-swiper-optionsからカスタムオプションを取得
    const customOptions = swiperEl.dataset.swiperOptions
        ? JSON.parse(swiperEl.dataset.swiperOptions)
        : {};

    // breakpointsのキーにTailwindのscreen名が使われていれば数値に変換
    if (customOptions.breakpoints) {
        customOptions.breakpoints = Object.fromEntries(
            Object.entries(customOptions.breakpoints).map(([key, value]) => {
                const screenValue = (screens as Record<string, string>)[key];
                if (!screenValue) return [key, value];
                const num = parseFloat(screenValue);
                const pxValue = /r?em$/.test(screenValue) ? Math.round(num * 16) : Math.round(num);
                return [pxValue, value];
            })
        );
    }

    // controllerオプションを初期化
    if (!customOptions.controller) {
        customOptions.controller = {};
    }

    // ナビゲーションボタンのオプション
    const nextButton = swiperEl.querySelector<HTMLElement>('.swiper-button-next');
    const prevButton = swiperEl.querySelector<HTMLElement>('.swiper-button-prev');

    if (nextButton && prevButton) {
        const isBlocked =
            nextButton.classList.contains('swiper-button-block') &&
            prevButton.classList.contains('swiper-button-block');

        if (!isBlocked) {
            loadSvg('./svg/next-button.svg').then(svg => { nextButton.innerHTML = svg; });
            loadSvg('./svg/prev-button.svg').then(svg => { prevButton.innerHTML = svg; });
        }

        customOptions.navigation = {
            nextEl: nextButton,
            prevEl: prevButton,
            ...(customOptions.navigation ?? {}),
        };
    }

    // ページネーションのオプション
    const swiperPagination = swiperEl.querySelector<HTMLElement>('.swiper-pagination');
    if (swiperPagination) {
        customOptions.pagination = {
            el: swiperPagination,
            clickable: true,
            ...(customOptions.pagination || {}),
        };
    }

    // スクローラーのオプション
    const swiperScrollbar = swiperEl.querySelector<HTMLElement>('.swiper-scrollbar');
    if (swiperScrollbar) {
        customOptions.scrollbar = {
            el: swiperScrollbar,
            draggable: true,
            ...(customOptions.scrollbar || {}),
        };
    }

    // activeクラスを持つスライドを初期スライドに設定
    let initialSlide = 0;
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    const activeSlide = swiperEl.querySelector('.swiper-slide.active');
    if (activeSlide) {
        initialSlide = Array.from(slides).indexOf(activeSlide);
    } else {
        const hasActiveSlide = swiperEl.querySelector('.swiper-slide .active');
        if (hasActiveSlide) {
            const slide = hasActiveSlide.closest('.swiper-slide');
            if (slide) {
                initialSlide = Array.from(slides).indexOf(slide);
            }
        }
    }

    return {
        ...commonOptions,
        ...{ initialSlide },
        ...customOptions,
    };
}

